import { useEffect, useState } from "react";
import { Spin, Table, Tag, Form, Switch, Card, Row, Col, Button } from "antd";
import { getAvailableChannels, getClientIntegrations, updateClientIntegration } from '@/ts/retrieve-data';
import { IClientListItem, IClientIntegration, IIntegrationChannel, ClientIntegrationFormFields, ClientIntegrationColumns, ClientIntegrationFields, ChannelIntegrationConfig } from "@/ts/common-types";
import { showToast } from "@/ts/modals";
import { ChannelIntegrations } from "./ChannelIntegrations";
import dayjs from "dayjs";
import { GrEdit } from "react-icons/gr";
import { RightOutlined } from "@ant-design/icons";
import React from "react";
import { DISABLED_CHANNELS, DateTimeFormats } from '@/ts/constants';
import DisconnectIntegrationModal from '@/components/modals/DisconnectIntegrationModal';
import EditClientChannelIntegrationModal from '@/components/modals/EditClientChannelIntegrationModal';
import { CHANNEL_CONFIGS } from '@/config/channel-configs';
import { usePermissionCheck, useUserTimezone } from '@/hooks/AllCustomHooks';
import { titleCase } from '@/ts/utils';
import { convertIsoDate } from '@/ts/utils';

import "@/scss/client-integrations.scss";

interface ClientIntegrationsProps {
    client: IClientListItem;
}

const FLIPKART_CHANNEL_IDS = [2, 28, 39];

const ClientIntegrations = ({ client }: ClientIntegrationsProps) => {
    const { canUpdateClient } = usePermissionCheck();
    const currentTimeZone = useUserTimezone(client.timezones?.[0]);
    const [channels, setChannels] = useState<IIntegrationChannel[]>([]);
    const [selectedChannel, setSelectedChannel] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const [integrations, setIntegrations] = useState<IClientIntegration[]>([]);
    const [integrationsLoading, setIntegrationsLoading] = useState(false);
    const [editingIntegration, setEditingIntegration] =
        useState<IClientIntegration | null>(null);
    const [form] = Form.useForm();
    const [showDisconnectModal, setShowDisconnectModal] = useState(false);
    const [disconnectingChannelId, setDisconnectingChannelId] = useState<
        number | null
    >(null);
    const [channelConfig, setChannelConfig] =
        useState<ChannelIntegrationConfig | null>(null);

    useEffect(() => {
        loadChannels();
        loadIntegrations();
    }, [client.country, client.id]);

    const loadChannels = async () => {
        setLoading(true);
        try {
            const response = await getAvailableChannels(client.country);
            if (response) {
                const mappedChannels: IIntegrationChannel[] = response.map(channel => {
                    const config = CHANNEL_CONFIGS[channel.channel_id];
                    
                    return {
                        channel_id: channel.channel_id,
                        name: channel.name,
                        logo: config?.logo,
                        description: `Integrate with ${titleCase(channel.name)}`,
                        platform_status: false,
                        oauthEnabled: config?.oauthEnabled || false
                    };
                });
                setChannels(mappedChannels);
            }
        } catch (error) {
            console.error("Error fetching channels:", error);
            showToast("danger", "Failed to fetch available channels");
        } finally {
            setLoading(false);
        }
    };

    const loadIntegrations = async () => {
        setIntegrationsLoading(true);
        try {
            const clientIntegrations = await getClientIntegrations(client.id);
            setIntegrations(clientIntegrations);
        } catch (error) {
            console.error("Error loading integrations:", error);
            showToast("danger", "Failed to load integrations");
        } finally {
            setIntegrationsLoading(false);
        }
    };

    const isChannelIntegrated = (channelId: number) => {
        return integrations.some(
            (integration) =>
                integration.channel_id === channelId &&
                integration.platform_status
        );
    };

    const handleEdit = (integration: IClientIntegration) => {
        if (!canUpdateClient) {
            showToast(
                "danger",
                "You do not have permission to edit integrations"
            );
            return;
        }

        const config = CHANNEL_CONFIGS[integration.channel_id];
        if (!config) {
            showToast(
                "danger",
                `Configuration for channel ID ${integration.channel_id} not found`
            );
            return;
        }
        setEditingIntegration(integration);
        setChannelConfig(config);

        const formValues: Record<string, any> = {
            [ClientIntegrationFormFields.PlatformStatus]:
                integration.platform_status,
            [ClientIntegrationFormFields.DefaultAccountName]:
                integration.default_account_name,
            [ClientIntegrationFormFields.JobPlatformName]:
                integration.job_platform_name,
        };

        // Add all auth fields from the config
        config.authFields.forEach(field => {
            const fieldName = field.id;
            formValues[fieldName] = integration[fieldName] || '';
        });

        form.setFieldsValue(formValues);
    };

    const handleEditCancel = () => {
        setEditingIntegration(null);
        form.resetFields();
    };

    const handleEditSubmit = async () => {
        try {
            const values = await form.validateFields();
            if (editingIntegration) {
                const payload = {
                    ...values,
                    ...(values.password ? { password: values.password } : {}),
                };
                await updateClientIntegration(
                    client.id,
                    editingIntegration.channel_id,
                    payload
                );
                showToast("success", "Integration updated successfully");
                loadIntegrations();
                handleEditCancel();
            }
        } catch (error) {
            console.error("Failed to update integration:", error);
            showToast("danger", "Failed to update integration");
        }
    };

    const handleToggleIntegration = async (
        channelId: number,
        currentStatus: boolean
    ) => {
        if (!canUpdateClient) {
            showToast(
                "danger",
                "You do not have permission to update integrations"
            );
            return;
        }

        if (currentStatus) {
            setDisconnectingChannelId(channelId);
            setShowDisconnectModal(true);
        } else {
            setSelectedChannel(channelId);
        }
    };

    const resetDisconnectModalState = () => {
        setShowDisconnectModal(false);
        setDisconnectingChannelId(null);
    };

    const handleDisconnectConfirm = async () => {
        if (disconnectingChannelId) {
            try {
                await updateClientIntegration(
                    client.id,
                    disconnectingChannelId,
                    {
                        platform_status: false,
                    }
                );
                loadIntegrations();
                showToast("success", "Integration disconnected successfully");
            } catch (error) {
                showToast("danger", "Failed to disconnect integration");
            }
            resetDisconnectModalState();
        }
    };

    const handleViewIntegration = (channelId: number) => {
        setSelectedChannel(channelId);
    };

    const renderIntegrationCards = () => {
        return (
            <Row gutter={[16, 16]}>
                {channels
                    .filter(
                        (channel) => !DISABLED_CHANNELS.includes(channel.channel_id)
                    )
                    .map((channel) => {
                        const isIntegrated = isChannelIntegrated(channel.channel_id);

                        return (
                            <Col xs={24} sm={12} md={8} lg={6} key={channel.channel_id}>
                                <Card className="integration-card">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        {channel.logo &&
                                            React.cloneElement(channel.logo, {
                                                width: 40,
                                                height: 40,
                                                className: "integration-logo",
                                            })}
                                        <Switch
                                            checked={isIntegrated}
                                            onChange={() =>
                                                handleToggleIntegration(
                                                    channel.channel_id,
                                                    isIntegrated
                                                )
                                            }
                                            disabled={!canUpdateClient}
                                        />
                                    </div>
                                    <h4>{titleCase(channel.name)}</h4>
                                    <p className="text-muted">{channel.description}</p>
                                    <Button 
                                        type="link" 
                                        onClick={() => handleViewIntegration(channel.channel_id)}
                                        className="d-flex align-items-center justify-content-between w-100 p-0"
                                    >
                                        View integration
                                        <RightOutlined />
                                    </Button>
                                </Card>
                            </Col>
                        );
                    })}
            </Row>
        );
    };

    const pastIntegrationsColumns = [
        {
            title: ClientIntegrationColumns.CHANNEL,
            dataIndex: ClientIntegrationFields.CHANNEL_NAME,
            key: ClientIntegrationFields.CHANNEL_NAME,
            sorter: (a: IClientIntegration, b: IClientIntegration) =>
                a.channel_name.localeCompare(b.channel_name),
        },
        {
            title: ClientIntegrationColumns.PLATFORM_NAME,
            dataIndex: ClientIntegrationFields.JOB_PLATFORM_NAME,
            key: ClientIntegrationFields.JOB_PLATFORM_NAME,
            sorter: (a: IClientIntegration, b: IClientIntegration) =>
                a.job_platform_name.localeCompare(b.job_platform_name),
        },
        {
            title: ClientIntegrationColumns.ACCOUNT_ID,
            key: ClientIntegrationFields.ACCOUNT_ID,
            render: (_: undefined, record: IClientIntegration) => {
                const config = CHANNEL_CONFIGS[record.channel_id];
                if (!config) return "-";

                // For Flipkart channels, primary account ID is fk_aa_account_id
                if (FLIPKART_CHANNEL_IDS.includes(record.channel_id)) {
                    return record.fk_aa_account_id || "-";
                } else {
                    // For other channels, primary account ID is unq_account_id
                    return record.unq_account_id || "-";
                }
            },
            sorter: (a: IClientIntegration, b: IClientIntegration) => {
                // For Flipkart channels, sort by fk_aa_account_id
                if (
                    FLIPKART_CHANNEL_IDS.includes(a.channel_id) &&
                    FLIPKART_CHANNEL_IDS.includes(b.channel_id)
                ) {
                    const valueA = a.fk_aa_account_id || "";
                    const valueB = b.fk_aa_account_id || "";
                    return valueA.localeCompare(valueB);
                }
                // For mixed comparison, convert to strings and compare
                else if (FLIPKART_CHANNEL_IDS.includes(a.channel_id)) {
                    const valueA = a.fk_aa_account_id || "";
                    const valueB = b.unq_account_id || "";
                    return valueA.localeCompare(valueB);
                } else if (FLIPKART_CHANNEL_IDS.includes(b.channel_id)) {
                    const valueA = a.unq_account_id || "";
                    const valueB = b.fk_aa_account_id || "";
                    return valueA.localeCompare(valueB);
                }
                // For other channels, sort by unq_account_id
                else {
                    const valueA = a.unq_account_id || "";
                    const valueB = b.unq_account_id || "";
                    return valueA.localeCompare(valueB);
                }
            },
        },
        {
            title: ClientIntegrationColumns.PARENT_ACCOUNT_ID,
            key: ClientIntegrationFields.PARENT_ACCOUNT_ID,
            render: (_: undefined, record: IClientIntegration) => {
                // For Flipkart channels, secondary account ID is fk_ba_account_id
                if (FLIPKART_CHANNEL_IDS.includes(record.channel_id)) {
                    return record.fk_ba_account_id || "-";
                } else {
                    return "-";
                }
            },
            sorter: (a: IClientIntegration, b: IClientIntegration) => {
                if (
                    FLIPKART_CHANNEL_IDS.includes(a.channel_id) &&
                    FLIPKART_CHANNEL_IDS.includes(b.channel_id)
                ) {
                    const valueA = a.fk_ba_account_id || "";
                    const valueB = b.fk_ba_account_id || "";
                    return valueA.localeCompare(valueB);
                } else if (FLIPKART_CHANNEL_IDS.includes(a.channel_id)) {
                    const valueA = a.fk_ba_account_id || "";
                    return valueA.localeCompare("");
                } else if (FLIPKART_CHANNEL_IDS.includes(b.channel_id)) {
                    const valueB = b.fk_ba_account_id || "";
                    return "".localeCompare(valueB);
                } else {
                    return 0;
                }
            },
        },
        {
            title: ClientIntegrationColumns.USERNAME,
            dataIndex: ClientIntegrationFields.ACCOUNT_USERNAME,
            key: ClientIntegrationFields.ACCOUNT_USERNAME,
            render: (username: string | null) => username || "-",
            sorter: (a: IClientIntegration, b: IClientIntegration) => {
                const usernameA = a.account_username || "";
                const usernameB = b.account_username || "";
                return usernameA.localeCompare(usernameB);
            },
        },
        {
            title: ClientIntegrationColumns.STATUS,
            dataIndex: ClientIntegrationFields.PLATFORM_STATUS,
            key: ClientIntegrationFields.PLATFORM_STATUS,
            render: (status: boolean) => (
                <Tag color={status ? "green" : "red"}>
                    {status ? "Active" : "Inactive"}
                </Tag>
            ),
            sorter: (a: IClientIntegration, b: IClientIntegration) =>
                Number(a.platform_status) - Number(b.platform_status),
        },
        {
            title: ClientIntegrationColumns.REGISTERED_ON,
            dataIndex: ClientIntegrationFields.REGISTERED_ON,
            key: ClientIntegrationFields.REGISTERED_ON,
            render: (date: string) => (
                <div>
                    <div>{convertIsoDate(String(date), currentTimeZone)}</div>
                    <div className="small text-muted">
                        {convertIsoDate(String(date))} {DateTimeFormats.UTC_SUFFIX}
                    </div>
                </div>
            ),
            sorter: (a: IClientIntegration, b: IClientIntegration) =>
                new Date(a.registered_on).getTime() -
                new Date(b.registered_on).getTime(),
        },
        ...(canUpdateClient
            ? [
                  {
                      title: ClientIntegrationColumns.ACTIONS,
                      key: ClientIntegrationFields.ACTIONS,
                      render: (_: undefined, record: IClientIntegration) => (
                          <Row>
                              <Col span={12}>
                                  <GrEdit
                                      style={{
                                          fontSize: "18px",
                                          cursor: "pointer",
                                      }}
                                      onClick={() => handleEdit(record)}
                                  />
                              </Col>
                          </Row>
                      ),
                  },
              ]
            : []),
    ];

    return (
        <div className="client-integrations-container">
            {selectedChannel ? (
                <ChannelIntegrations
                    client={client}
                    channelId={selectedChannel}
                    onBack={() => setSelectedChannel(null)}
                    isIntegrated={isChannelIntegrated(selectedChannel)}
                    onDisconnect={loadIntegrations}
                />
            ) : (
                <div className="card shadow mb-4">
                    <div className="p-4" style={{ cursor: "default" }}>
                        <h4 className="mb-4">Available Integrations</h4>
                        {loading ? (
                            <div className="text-center">
                                <Spin />
                            </div>
                        ) : (
                            renderIntegrationCards()
                        )}
                    </div>
                </div>
            )}

            <div className="card shadow">
                <div className="p-4" style={{ cursor: "default" }}>
                    <h4 className="mb-3">Past Integrations</h4>
                    <div className="table-responsive">
                        <Table
                            columns={pastIntegrationsColumns}
                            dataSource={selectedChannel 
                                ? integrations.filter(integration => integration.channel_id === selectedChannel)
                                : integrations}
                            loading={integrationsLoading}
                            rowKey={(record) =>
                                `${record.channel_id}-${record.default_account_name}-${record.registered_on}`
                            }
                            pagination={{ pageSize: 10 }}
                            scroll={{ x: "max-content" }}
                        />
                    </div>
                </div>
            </div>

            <DisconnectIntegrationModal
                isOpen={showDisconnectModal}
                onCancel={resetDisconnectModalState}
                onConfirm={handleDisconnectConfirm}
            />

            <EditClientChannelIntegrationModal
                isOpen={!!editingIntegration}
                onCancel={handleEditCancel}
                onSubmit={handleEditSubmit}
                editingIntegration={editingIntegration}
                form={form}
                channelConfig={channelConfig}
            />
        </div>
    );
};

export default ClientIntegrations;
